import { useState } from 'react';
import { Info, MagnifyingGlass, Plus, X } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ConfirmationModal } from '@/components/core';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { TemplatesTable } from '../../components/templates-table/templates-table';
import { useDeleteTemplate, useSearchTemplates } from '../../hooks/use-templates';
import {
  TEMPLATE_CATEGORIES,
  type TemplateCategory,
  type TemplateListItem,
} from '../../types/templates.types';
import { getTemplateErrorMessage } from '../../utils/template-api-errors';

const ALL_CATEGORIES = 'All categories';

export const TemplatesPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>(ALL_CATEGORIES);
  const [templateToDelete, setTemplateToDelete] = useState<TemplateListItem | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { data, isLoading, isError, error, refetch } = useSearchTemplates(search, {
    category: category === ALL_CATEGORIES ? undefined : (category as TemplateCategory),
    sort: 'updated',
  });
  const { mutateAsync: deleteTemplate, isPending: isDeleting } = useDeleteTemplate();

  const templates = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const hasActiveFilters = search.trim().length > 0 || category !== ALL_CATEGORIES;

  const handleClearFilters = () => {
    setSearch('');
    setCategory(ALL_CATEGORIES);
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      await deleteTemplate(templateToDelete.itemId);

      toast({
        variant: 'success',
        title: 'Template deleted',
        description: `"${templateToDelete.name.trim() || 'Untitled template'}" was deleted. Campaigns already built from it keep their own copy and won't change.`,
      });
      setIsDeleteConfirmOpen(false);
      setTemplateToDelete(null);
    } catch (deleteError) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete template',
        description: getTemplateErrorMessage(deleteError, 'Unexpected error while deleting.'),
      });
    }
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 w-full max-w-full flex-col gap-5 overflow-hidden">
      <div
        className="flex items-start gap-2.5 rounded-lg px-3.5 py-2.5"
        style={{ background: '#E6F0F8' }}
      >
        <Info className="mt-0.5 size-4 shrink-0 text-primary-600" aria-hidden />
        <p className="text-[13px] leading-[18px]" style={{ color: '#003F6F' }}>
          Templates are <strong>copied, not linked</strong>. Applying a template to a campaign
          copies its current content once — editing or deleting this template afterwards never
          changes a campaign already built from it, and vice versa. See{' '}
          <span className="font-mono">Decision-Template-and-Content-Storage-Model</span>.
        </p>
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <div>
          <h1 className="text-[28px] font-semibold leading-9 tracking-tight text-neutral-900">
            Templates
          </h1>
          <p className="mt-0.5 text-neutral-600">
            Reusable starting points for campaign content — build one once, reuse it whenever you
            like.
          </p>
        </div>
        <div className="flex-1" />
        <Button
          type="button"
          className="h-10 gap-2 rounded-md bg-primary-600 px-4 font-medium text-white hover:bg-primary-700"
          onClick={() => navigate('/templates/create')}
        >
          <Plus className="size-4" aria-hidden />
          New template
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[260px] max-w-[360px] flex-1">
          <MagnifyingGlass
            className="pointer-events-none absolute left-3 top-1/2 size-[15px] -translate-y-1/2 text-neutral-500"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search templates by name"
            className="h-10 rounded-md border-neutral-200 bg-white pl-[34px] pr-10 text-sm"
            aria-label="Search templates by name"
          />
          {search ? (
            <button
              type="button"
              className="absolute right-2 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
              aria-label="Clear template search"
              onClick={() => setSearch('')}
            >
              <X className="size-4" aria-hidden />
            </button>
          ) : null}
        </div>

        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger
            className="h-10 min-w-[170px] w-auto bg-white"
            aria-label="Filter templates by category"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CATEGORIES}>All categories</SelectItem>
            {TEMPLATE_CATEGORIES.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex-1" />
        <span className="whitespace-nowrap text-[13px] leading-[18px] text-neutral-500">
          {totalCount} {totalCount === 1 ? 'template' : 'templates'}
        </span>
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-e1">
        <div className="min-h-0 flex-1 overflow-auto">
          <TemplatesTable
            templates={templates}
            isLoading={isLoading}
            isError={isError}
            errorMessage={
              error
                ? getTemplateErrorMessage(
                    error,
                    'Something went wrong while fetching your template library. Check your connection and try again.'
                  )
                : undefined
            }
            hasActiveFilters={hasActiveFilters}
            onRetry={() => {
              void refetch();
            }}
            onClearFilters={handleClearFilters}
            onCreate={() => navigate('/templates/create')}
            onDelete={(template) => {
              setTemplateToDelete(template);
              setIsDeleteConfirmOpen(true);
            }}
          />
        </div>
      </div>

      <ConfirmationModal
        open={isDeleteConfirmOpen}
        onOpenChange={(open) => {
          if (isDeleting) return;
          setIsDeleteConfirmOpen(open);
          if (!open) setTemplateToDelete(null);
        }}
        title={`Delete “${templateToDelete?.name.trim() || 'this template'}”?`}
        description="This removes the template from the library. Deleting it is not the same as breaking anything already built — campaigns already built from it keep their own copy and will not change."
        confirmText={isDeleting ? 'Deleting…' : 'Delete template'}
        cancelText="Cancel"
        confirmClassName="bg-destructive hover:bg-destructive/90 text-white"
        preventAutoClose
        onConfirm={() => {
          void handleDeleteTemplate();
        }}
      />
    </div>
  );
};
